import React, { useState } from "react";
import styles from "./static-separator-editor.module.scss";
import { useI18N } from "@src/language/language-context";
import {
  ColorSwatchPicker,
  DEFAULT_TREND_LINE_COLOR,
  TREND_LINE_PALETTE,
  pickPaletteHex,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useSaveWidget } from "../hooks/use-save-widget";
import { Widget } from "@src/types/dashboards";

const cssClassName = "static-separator-editor";

export interface StaticSeparatorEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

export const StaticSeparatorEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: StaticSeparatorEditorProps) => {
  const i18n = useI18N();

  const [color, setColor] = useState<string>(
    () =>
      widget.presentation?.color ??
      (isPendingWidget ? pickPaletteHex() : DEFAULT_TREND_LINE_COLOR),
  );
  const [transparent, setTransparent] = useState<boolean | undefined | null>(
    () => (isPendingWidget ? true : widget.presentation?.transparent),
  );

  const texts = {
    fontColorLabel: i18n("modules.staticSeparator.fontColorLabel"),
    transparentLabel: i18n("modules.staticSeparator.transparentLabel"),
    saveButton: i18n("modules.staticSeparator.saveButton"),
    cancelButton: i18n("modules.staticSeparator.cancelButton"),
  };

  const validate = (): string | null => null;

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    const presentation = {
      ...widget.presentation,
      color,
      transparent: transparent ? true : undefined,
    };
    save(
      {
        ...widget,
        image_url: widget.image_url ?? null,
        presentation,
      },
      { validate, imageFile: null },
    );
  };

  return (
    <div className={styles[`${cssClassName}__outer-wrapper`]}>
      <div className={styles[`${cssClassName}__field`]}>
        <ColorSwatchPicker
          label={texts.fontColorLabel}
          value={color}
          swatches={TREND_LINE_PALETTE}
          onChange={setColor}
        />
      </div>
      <CheckBoxWithText
        text={texts.transparentLabel}
        checkboxState={transparent}
        handleCheckBoxClick={() => setTransparent((prev) => !prev)}
      />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
